import { parseMeetingLink } from '../../../shared/external-meeting-links';
import { ACCOUNT_CALL_ELSEWHERE_CHANGED, CALL_END_BEFORE_NAVIGATION, EXTERNAL_MEETING_CALL_ENDED, MOVE_CALL_HERE, RESUME_HELD_CALL, SWITCH_TO_HELD_CALL, END_HELD_CALL, MERGE_HELD_CALL, REQUEST_INTERPRETER, START_CALL_AFTER_BUSY, CANCEL_REQUEST, MATCH_FOUND, MEETING_INITIATED, INTERPRETED_CALL_ENDED, QUEUE_STATUS_UPDATE, QUEUE_CONNECTION_CHANGED, INTERPRETER_AVAILABILITY_CHANGED, INTERPRETER_BREAK_CHANGED, INTERPRETER_REQUEST_RECEIVED, INTERPRETER_SESSION_SUPERSEDED, INTERPRETER_WRAP_UP_UPDATED, INTERPRETER_MEETING_STEP_SENT, QUEUE_SERVICE_ERROR, SET_PAUSE_AFTER_CALL } from './actionTypes';
import { queueService } from './InterpreterQueueService';
import { forgetInterpreterRequest, lastInterpreterRequest, rememberInterpreterRequest, requestOwnerKey, retryRequestArgs } from './lastInterpreterRequest';
import { getPersistentItem, getPersistentJson } from '../vrs-auth/storage';
import { noteTransferAction } from './transferOffers';
import { getLocalParticipant } from '../base/participants/functions';
import { mobileLog } from '../mobile/navigation/logging';
import { emitMobileTelemetryEvent } from '../mobile/navigation/telemetry';
/**
 * Creates an action to request an interpreter.
 *
 * @param {string} language - The requested language (e.g., 'ASL').
 * @param {RequestInterpreterOptions} options - Optional service-mode and invite metadata.
 * @returns {Function}
 */
export function requestInterpreter(language = 'ASL', options = {}) {
    return (dispatch, getState) => {
        const localParticipant = getLocalParticipant(getState());
        const clientName = localParticipant?.name || localParticipant?.displayName || 'Guest';
        // Generate a unique request ID
        const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        const canAuthenticate = queueService.canAuthenticate();
        mobileLog('info', 'queue_request_interpreter_action', {
            canAuthenticate,
            callType: options.callType || 'vrs',
            hasCallerIdPhone: Boolean(options.callerIdPhone),
            hasTargetPhone: Boolean(options.targetPhone),
            vcoEnabled: Boolean(options.vcoEnabled),
            queueConnected: queueService.isConnected()
        }, { console: false });
        emitMobileTelemetryEvent('queue_request_interpreter_action', {
            canAuthenticate,
            callType: options.callType || 'vrs',
            hasCallerIdPhone: Boolean(options.callerIdPhone),
            hasTargetPhone: Boolean(options.targetPhone),
            vcoEnabled: Boolean(options.vcoEnabled),
            queueConnected: queueService.isConnected()
        }, {
            requestId,
            roomName: options.roomName
        });
        if (!canAuthenticate) {
            dispatch({
                type: QUEUE_SERVICE_ERROR,
                error: 'Session expired. Sign in again.',
                operation: 'request_interpreter'
            });
            return;
        }
        // "Try again" sends this same request again (lastInterpreterRequest.ts).
        rememberInterpreterRequest(language, options, currentRequestOwner());
        // Dispatch action to update local state
        const meeting = options.externalMeetingUrl ? parseMeetingLink(options.externalMeetingUrl) : null;
        const reminder = options.externalMeetingReminder || null;
        dispatch({
            type: REQUEST_INTERPRETER,
            at: Date.now(),
            requestId,
            language,
            clientName,
            externalMeeting: meeting?.ok ? { host: meeting.host, platform: meeting.platform }
                : reminder ? { host: reminder.host, platform: reminder.platform } : undefined
        });
        // Send request to queue service. The service buffers the message and
        // delivers it after the socket authenticates, so a cold start or an
        // in-flight token refresh does not silently drop the request.
        if (!queueService.isConnected()) {
            console.warn('Queue service not connected - request buffered until authentication completes');
        }
        const { externalMeetingReminder, ...sendOptions } = options;
        queueService.requestInterpreter(language, clientName, {
            ...sendOptions,
            externalMeetingReminderId: externalMeetingReminder?.id || undefined
        });
    };
}
/**
 * The signed-in account that owns a request now (lastInterpreterRequest.ts).
 *
 * @returns {string|null}
 */
export function currentRequestOwner() {
    const user = getPersistentJson('vrs_user_info');
    return requestOwnerKey(user, getPersistentItem('vrs_user_role') || user?.role);
}
/**
 * "Try again" on "No interpreter is available.": sends the client's last
 * request again, the same language and options, in one tap.
 *
 * @returns {Function} A thunk that returns true when it sent the request.
 */
export function retryInterpreterRequest() {
    return (dispatch, getState) => {
        const args = retryRequestArgs(lastInterpreterRequest(), Boolean(getState()['features/interpreter-queue']?.isRequestPending), currentRequestOwner());
        if (!args) {
            // Another account, or nothing kept: never send someone else's request.
            forgetInterpreterRequest();
            return false;
        }
        mobileLog('info', 'queue_request_try_again', { callType: args[1].callType || 'vrs' }, { console: false });
        dispatch(requestInterpreter(...args));
        return true;
    };
}
/**
 * Ends interpreting in the client's own meeting (there is no room to leave).
 *
 * @param {string} callId - The external-meeting call.
 * @param {string} [roomName] - Its server room name, if known.
 * @returns {Function}
 */
export function endExternalMeetingCall(callId, roomName) {
    return (dispatch) => {
        queueService.endCall(callId, roomName);
        dispatch({ type: EXTERNAL_MEETING_CALL_ENDED, callId });
    };
}
/**
 * Malka Terp reports one step of the interpreter's outside-meeting call
 * (meetingRemote.ts). The card shows the step as sent until the server
 * answers: external_meeting_status_ack, call_ended after 'ended', or an error.
 *
 * @param {string} callId - The outside-meeting call.
 * @param {MeetingStatusEvent} event - The step.
 * @param {MeetingEndReason} [endReason] - Why the interpreting ended ('ended' only).
 * @returns {Function}
 */
export function reportMeetingStep(callId, event, endReason) {
    return (dispatch) => {
        dispatch({ type: INTERPRETER_MEETING_STEP_SENT, callId, event });
        queueService.sendExternalMeetingStatus(callId, event, event === 'ended' ? endReason : undefined);
        mobileLog('info', 'interpreter_meeting_step_sent', { event, ...endReason ? { endReason } : {} }, { console: false });
    };
}
/**
 * Creates an action to cancel a pending interpreter request.
 *
 * @returns {Function}
 */
export function cancelInterpreterRequest() {
    return (dispatch, getState) => {
        const requestId = getState()['features/interpreter-queue']?.currentRequestId;
        dispatch({
            type: CANCEL_REQUEST
        });
        // The person cancelled: there is nothing to try again.
        forgetInterpreterRequest();
        queueService.cancelRequest(requestId);
    };
}
/**
 * Creates an action to accept an interpreter request.
 *
 * @param {string} requestId - The ID of the request to accept.
 * @returns {Function}
 */
export function acceptInterpreterRequest(requestId) {
    return (_dispatch, getState) => {
        const state = getState();
        const request = state['features/interpreter-queue']?.pendingRequests?.find((r) => r.id === requestId);
        // A colleague's team request: team_joined then opens the room (middleware).
        if (request?.team) {
            queueService.acceptTeam(request.team.teamId);
            return;
        }
        // A colleague's warm transfer answers with its own message; the
        // server then reuses the queue-accept room path.
        if (request?.transfer) {
            noteTransferAction({ at: Date.now(), cardId: request.id, kind: 'accept' });
            queueService.acceptCallTransfer(request.transfer.transferId);
            return;
        }
        // Keep the offer visible until request_accepted/request_withdrawn.
        // Removing it on click hides correlated server failures and prevents a
        // safe retry after a transient or authorization error.
        if (request?.roomName) {
            queueService.acceptRequest(requestId, request.roomName, request.offerId);
        }
        else {
            queueService.acceptRequest(requestId, undefined, request?.offerId);
        }
    };
}
/**
 * Creates an action to decline an interpreter request.
 *
 * @param {string} requestId - The ID of the request to decline.
 * @returns {Function}
 */
export function declineInterpreterRequest(requestId) {
    return (_dispatch, getState) => {
        const request = getState()['features/interpreter-queue']?.pendingRequests?.find((r) => r.id === requestId);
        // A declined team request: team_offer_ended (declined) takes the card down.
        if (request?.team) {
            queueService.declineTeam(request.team.teamId);
            return;
        }
        // Declining a transfer leaves the call with the colleague; the server
        // acknowledges with call_transfer_cancelled and the middleware removes
        // the card.
        if (request?.transfer) {
            noteTransferAction({ at: Date.now(), cardId: request.id, kind: 'decline' });
            queueService.declineCallTransfer(request.transfer.transferId);
            return;
        }
        // The server acknowledges request_declined and the middleware removes
        // the exact card. Until then the UI remains busy instead of pretending
        // the offer was processed.
        queueService.declineRequest(requestId, request?.offerId);
    };
}
/**
 * Action dispatched when a new interpreter request is received (for interpreters).
 */
export function interpreterRequestReceived(request) {
    return {
        type: INTERPRETER_REQUEST_RECEIVED,
        request
    };
}
/**
 * Action dispatched when a match is found.
 */
export function matchFound(matchData) {
    // Answered: the request is done, so "Try again" can never send it again.
    forgetInterpreterRequest();
    return {
        type: MATCH_FOUND,
        matchData
    };
}
/**
 * Action dispatched when a meeting is initiated.
 */
export function meetingInitiated(meetingData) {
    return {
        type: MEETING_INITIATED,
        meetingData
    };
}
/**
 * Action dispatched when an interpreted call is no longer active.
 */
/**
 * Ends the stored call without the hang-up navigation: the caller navigates
 * next (the VideoMail room). See CALL_END_BEFORE_NAVIGATION.
 *
 * @param {string} reason - Logged with the exit.
 * @returns {Object}
 */
export function endCallBeforeNavigation(reason) {
    return {
        type: CALL_END_BEFORE_NAVIGATION,
        reason
    };
}
export function interpretedCallEnded(reason) {
    return {
        type: INTERPRETED_CALL_ENDED,
        reason
    };
}
/**
 * Action dispatched when queue status is updated.
 */
export function queueStatusUpdate(status) {
    return {
        type: QUEUE_STATUS_UPDATE,
        status
    };
}
/**
 * Action dispatched when queue connection status changes.
 */
export function queueConnectionChanged(connected, error) {
    return {
        type: QUEUE_CONNECTION_CHANGED,
        connected,
        error
    };
}
/**
 * The interpreter's in-call "Pause after this call" switch. Read once, when
 * the call ends, by the wrap-up (see wrapUp.ts).
 */
export function setPauseAfterCall(enabled) {
    return {
        type: SET_PAUSE_AFTER_CALL,
        enabled
    };
}
/**
 * Replace (or with undefined, end) the interpreter's wrap-up between calls.
 */
export function interpreterWrapUpUpdated(wrapUp) {
    return {
        type: INTERPRETER_WRAP_UP_UPDATED,
        wrapUp
    };
}
/**
 * The server confirmed whether this interpreter is in the queue. Drives Malka
 * Terp's keep-awake while Available.
 */
/**
 * The server's word on availability. `at` is when it arrived: the server
 * re-queues an interpreter at the back each time it confirms Available, so
 * this is when the current wait began.
 *
 * @param {boolean} available - Whether the server confirmed Available.
 * @param {number} at - When the confirmation arrived.
 * @returns {Object} The action.
 */
export function interpreterAvailabilityChanged(available, at = Date.now()) {
    return {
        type: INTERPRETER_AVAILABILITY_CHANGED,
        available,
        at
    };
}
/**
 * This interpreter's queue session went to another device
 * (session_superseded): this one no longer receives offers. `superseded`
 * false: this device took it back, or signed out.
 *
 * @param {boolean} superseded - Whether the session is now elsewhere.
 * @param {string} [message] - The server's words, shown as data.
 * @param {number} at - When it happened.
 * @returns {Object} The action.
 */
export function interpreterSessionSuperseded(superseded, message, at = Date.now()) {
    return {
        type: INTERPRETER_SESSION_SUPERSEDED,
        superseded,
        message,
        at
    };
}
/**
 * Whether Malka Terp's interpreter is on a break (Home loads and changes it).
 *
 * @param {boolean} onBreak - An active break is running.
 * @returns {Object} The action.
 */
export function interpreterBreakChanged(onBreak) {
    return {
        type: INTERPRETER_BREAK_CHANGED,
        onBreak
    };
}
/**
 * The client's call is open on another device of the account, or (undefined)
 * no longer is.
 *
 * @param {CallElsewhere} [callElsewhere] - Which call, where, since when.
 * @returns {Object} The action.
 */
export function accountCallElsewhereChanged(callElsewhere) {
    return {
        type: ACCOUNT_CALL_ELSEWHERE_CHANGED,
        callElsewhere
    };
}
/**
 * "Move call here": this device joins the account's call and the server
 * moves it here once this device's media is up (queue middleware).
 *
 * @param {Object} call - The call and its room.
 * @returns {Object} The action.
 */
export function moveCallHere(call) {
    return {
        type: MOVE_CALL_HERE,
        callId: call.callId,
        roomName: call.roomName
    };
}
/**
 * "Resume": back into the call this device held to answer another
 * (call-management heldCall); it comes off hold for the others.
 *
 * @param {string} callId - The held call.
 * @returns {Object} The action.
 */
export function resumeHeldCall(callId) {
    return {
        type: RESUME_HELD_CALL,
        callId
    };
}
/**
 * From inside a call, back to the held one (Robert, 2026-10-12: "yes other
 * call should go on hold"). 'hold' puts the current call on hold (Swap);
 * 'end' ends it (a call with an interpreter is never held).
 *
 * @param {string} callId - The held call.
 * @param {string} mode - What happens to the current call.
 * @returns {Object} The action.
 */
export function switchToHeldCall(callId, mode) {
    return {
        type: SWITCH_TO_HELD_CALL,
        callId,
        mode
    };
}
/**
 * End the call this device holds, from the in-call bar.
 *
 * @param {string} callId - The held call.
 * @returns {Object} The action.
 */
export function endHeldCall(callId) {
    return {
        type: END_HELD_CALL,
        callId
    };
}
/**
 * Merge the call this device holds into the one it is in (Robert,
 * 2026-10-12): its people join this call. P2P and group calls only.
 *
 * @param {string} callId - The held call.
 * @returns {Object} The action.
 */
export function mergeHeldCall(callId) {
    return {
        type: MERGE_HELD_CALL,
        callId
    };
}
/**
 * Hold and call, End and call, or End it and call (Robert, 2026-10-13): the
 * call in the way is held or ended, then `place` sends the new call. The
 * offer comes from checkCallBusy; the middleware checks it again.
 *
 * @param {CallBusyResult} busy - The busy result with its offer.
 * @param {Function} place - Sends the new call, as the screen would have.
 * @returns {Object} The action.
 */
export function startCallAfterBusy(busy, place) {
    return {
        type: START_CALL_AFTER_BUSY,
        offer: busy.offer,
        openCall: busy.openCall,
        place
    };
}
